<template>
  <div
    class="sidebar"
    :class="{show: isSideBarVisible || isHomeSideBarShowing, disabled: isDisabled, eu: isEURegion}"
  >
    <div class="flex-grow">
      <div
        class="sidenav-profile"
      >
        <aha-avatar
          :name="`${$store.state.currentUser.firstName} ${$store.state.currentUser.lastName}`"
          :image="$store.state.currentUser.avatar"
          :id="$store.state.currentUser.id"
          :width="40"
          :height="40"
          class="mr-3 text-lg"
        />
        <div>
          <p class="font-bold truncate max-w-32">
            {{ currentUser.firstName }} {{ currentUser.lastName }}
          </p>
          <p class="text-xs truncate sm:max-w-32 max-w-28">
            {{ currentUser.email }}
          </p>
        </div>
      </div>
      <div>
        <ul v-if="!hasOnboardingV3">
          <WorkspaceSection v-if="!isEURegion" />
          <div class="border-b border-secondary-60"></div>
          <div
            v-if="workingMode === 'team' && !isEURegion"
            class="m-2"
          >
            <router-link
              to="/apps/team/workspace"
              tag="li"
              class="sidebar__item"
              active-class="sidebar__item--active"
              @click.native="clickTeamPresentations"
            >
              <aha-icon
                name="presentation"
                viewBox="0 0 24 24"
                width="18"
                height="24"
              />
              <span class="sidebar__item-name">{{ $i18nPresenter.t('Presentations') }}</span>
            </router-link>
            <router-link
              to="/apps/team/members"
              tag="li"
              class="sidebar__item"
              active-class="sidebar__item--active"
              @click.native="clickTeamMembers"
            >
              <font-awesome-icon icon="users" />
              <span class="sidebar__item-name">{{ $i18nPresenter.t('Team members') }}</span>
            </router-link>
          </div>
          <div
            v-if="workingMode === 'personal'"
            class="m-2"
          >
            <router-link
              to="/apps/presentations"
              tag="li"
              class="sidebar__item"
              active-class="sidebar__item--active"
              @click.native="hideSide"
            >
              <aha-icon
                name="presentation"
                viewBox="0 0 24 24"
                width="18"
                height="24"
              />
              <span class="sidebar__item-name">{{ $i18nPresenter.t('My Presentations') }}</span>
            </router-link>
            <router-link
              to="/apps/template-library?from=leftSidebar"
              tag="li"
              class="sidebar__item"
              active-class="sidebar__item--active"
              @click.native="hideSide"
            >
              <aha-icon
                width="18"
                height="18"
                viewBox="0 0 24 24"
                name="inspiration"
              />
              <span class="sidebar__item-name">{{ $i18nPresenter.t('Templates') }}</span>
            </router-link>
            <router-link
              to="/apps/shared-presentations"
              tag="li"
              class="sidebar__item"
              active-class="sidebar__item--active"
              @click.native="hideSide"
              v-if="tabShareWithMeVisible"
            >
              <aha-icon
                name="shared-folder"
                width="18"
                height="18"
                viewBox="0 0 20 20"
              />
              <span class="sidebar__item-name">{{ $i18nPresenter.t('Shared with Me') }}</span>
            </router-link>
            <router-link
              to="/apps/user-plan"
              tag="div"
              class="sidebar__item"
              active-class="sidebar__item--active"
              v-if="currentUser.identifyProvider !== 'skoletube'"
              @click.native="hideSide"
            >
              <aha-icon
                name="bill"
                width="18"
                height="18"
                viewBox="0 0 24 24"
              />
              <span class="sidebar__item-name">{{ $i18nPresenter.t('My Plan') }}</span>
            </router-link>
            <router-link
              to=""
              tag="li"
              class="sidebar__item"
              v-if="currentUser.identifyProvider !== 'skoletube'
                && isCasualUserNotHaveTeam
                && !isEURegion"
              @click.native="clickCreateNewTeam"
            >
              <font-awesome-icon icon="users" />
              <img
                class="absolute"
                src="@/assets/images/icon/new-feature-sparkling.svg"
                width="24"
              />
              <span
                class="sidebar__item-name"
              >{{ $i18nPresenter.t('Create a team') }}</span>
            </router-link>
            <router-link
              to="/apps/white-label"
              tag="div"
              class="sidebar__item"
              active-class="sidebar__item--active"
              v-if="
                currentUser.identifyProvider !== 'skoletube'
                  && currentUser.plan == 'white-label'
              "
              @click.native="hideSide"
            >
              <font-awesome-icon icon="tag" fixed-width size="lg" />
              <span class="sidebar__item-name">{{ $i18nPresenter.t('White Label') }}</span>
            </router-link>
          </div>
        </ul>
        <ul v-else>
          <router-link
            to="/apps/user-plan"
            tag="div"
            class="sidebar__item"
            active-class="sidebar__item--active"
            v-if="currentUser.identifyProvider !== 'skoletube'"
            @click.native="hideSide"
          >
            <aha-icon name="bill" />
            <span class="sidebar__item-name">{{ $i18nPresenter.t('My Plan') }}</span>
          </router-link>
        </ul>
      </div>

      <div class="border-b border-secondary-60" />

      <div class="m-2" v-if="!isPresenterWhiteLabel && !hasOnboardingV3">
        <h5 v-b-toggle.collapse-resources class="sidebar__title has-collapse">
          <span
            class="text"
            :class="{
              'has-new': $store.state.hasNews
            }"
          >{{ $i18nPresenter.t('Resources') }}</span>

          <aha-icon
            class="icon-caret"
            name="arrow"
            width="14"
            height="14"
            viewBox="0 0 24 24"
          />
        </h5>
        <b-collapse :visible="isShowBCollapse" id="collapse-resources">
          <li>
            <a
              href="https://www.youtube.com/channel/UC6ZZtv3C9bveCE8E8tBPhPA"
              class="sidebar__item"
              target="_blank"
              @click="clickVideo"
            >
              <aha-icon
                name="videos"
                width="18"
                height="18"
                viewBox="0 0 24 24"
              />
              <span class="flex-grow sidebar__item-name">{{ $i18nPresenter.t('Videos') }}</span>
              <aha-icon name="open-new-window" class="text-base-80 new-window-icon" />
            </a>
          </li>
          <li v-if="false">
            <a
              href="https://ahaslides.com/blog/category/announcements/"
              class="sidebar__item"
              target="_blank"
              @click="clickWhatsNew"
            >
              <aha-icon
                v-if="$store.state.hasNews"
                name="whats-new_red"
                class="bell-ring"
                width="19"
                height="18"
                viewBox="0 0 28 24"
              />
              <aha-icon
                v-else
                name="whats-new"
                width="16"
                height="16"
                viewBox="0 0 24 24"
              />
              <span
                v-if="$store.state.hasNews"
                class="flex-grow sidebar__item-name"
              >{{ $i18nPresenter.t('What\'s New') }}</span>
              <span
                v-else
                class="flex-grow sidebar__item-name"
              >{{ $i18nPresenter.t('What\'s New') }}</span>
              <aha-icon name="open-new-window" class="text-base-80 new-window-icon" />
            </a>
          </li>
          <!-- <router-link
            to="/apps/community"
            tag="li"
            class="sidebar__item"
            active-class="sidebar__item--active"
            @click.native="hideSide()"
          >
            <aha-icon
              name="community"
              width="18"
              height="18"
              viewBox="0 0 24 24"
            />
            <span class="sidebar__item-name">Community</span>
          </router-link> -->
          <li>
            <a
              href="https://help.ahaslides.com/portal/en/community/ahaslides/filter/idea/"
              class="sidebar__item"
              target="_blank"
              @click="clickFeatureRequest"
            >
              <aha-icon
                name="lightbulb"
                width="18"
                height="18"
                viewBox="-6 0 24 18"
              />
              <span class="flex-grow sidebar__item-name">{{ $i18nPresenter.t('Request a Feature') }}</span>
            </a>
          </li>
          <li>
            <a
              href="https://help.ahaslides.com/"
              class="sidebar__item"
              target="_blank"
              @click="clickHelp"
            >
              <aha-icon
                name="question"
                width="18"
                height="18"
                viewBox="0 0 24 24"
              />
              <span class="flex-grow sidebar__item-name">{{ $i18nPresenter.t('Help Centre') }}</span>
              <aha-icon name="open-new-window" class="text-base-80 new-window-icon" />
            </a>
          </li>
        </b-collapse>
      </div>

      <div class="border-b border-secondary-60" />

      <!-- Only visible on mobile -->
      <div class="m-3 mt-3 mb-2 lg:hidden">
        <h5 class="sidebar__title">
          {{ $i18nPresenter.t('My account') }}
        </h5>

        <router-link
          to="/apps/user-profile"
          tag="div"
          class="sidebar__item"
          active-class="sidebar__item--active"
          v-if="currentUser.identifyProvider !== 'skoletube'"
          @click.native="hideSide"
        >
          <aha-icon
            name="profile"
            width="18"
            height="18"
            viewBox="0 0 24 24"
          />
          <span class="sidebar__item-name">{{ $i18nPresenter.t('My Profile') }}</span>
        </router-link>
        <router-link
          to="/apps/billing"
          tag="div"
          class="sidebar__item"
          active-class="sidebar__item--active"
          v-if="currentUser.identifyProvider !== 'skoletube' && !hasOnboardingV3"
          @click.native="hideSide"
        >
          <aha-icon
            width="18"
            height="18"
            viewBox="0 0 24 24"
            name="payment"
          />
          <span class="sidebar__item-name">{{ $i18nPresenter.t('Payment') }}</span>
        </router-link>

        <div class="sidebar__item" @click="handleLogoutButtonClick">
          <aha-icon
            name="logout"
            width="18"
            height="18"
            viewBox="0 0 24 24"
          />
          <span class="sidebar__item-name">{{ $i18nPresenter.t('Log out') }}</span>
        </div>
      </div>
    </div>

    <StickerCta
      open-in-new-tab
      v-if="currentUser.id && sticker"
      :sticker="sticker"
    />

    <div class="py-5 text-xs text-center" v-if="!isPresenterWhiteLabel">
      <a
        href="https://ahaslides.com"
        target="AhaSlides_HomePage"
        class="text-link--white"
      >
        {{ $i18nPresenter.t('Powered by AhaSlides') }}
      </a>
      -
      <a
        href="https://ahaslides.com/terms/"
        target="AhaSlides_Terms"
        class="text-link--white"
      >{{ $i18nPresenter.t('Terms') }}</a>
    </div>
    <modal-create-new-team
      v-if="showCreateTeamModal"
      @hide="hideCreatTeamModal"
    />
  </div>
</template>

<script>
import moment from 'moment'
import * as Sentry from '@sentry/vue'
import { mapState, mapActions } from 'vuex'
import utils from '@/util'
import constants from '@/constant'
import config from '@/config'
// api
import StickerApi from '@/api/sticker'

// components
import StickerCta from './StickerCta.vue'
import ModalCreateNewTeam from '@/components-v2/modals/CreatNewTeam.vue'
import WorkspaceSection from './Workspace.vue'

const { eventCategories } = constants

export default {
  components: {
    WorkspaceSection,
    StickerCta,
    ModalCreateNewTeam,
  },
  props: {
    isSideBarVisible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isShowingSideBar: false,
      sticker: null,
      showCreateTeamModal: false,
      isShowBCollapse: false,
    }
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.currentUser,
      plan: (state) => state.plan,
      presentations: (state) => state.presentations,
      isPresenterWhiteLabel: (state) => state.isPresenterWhiteLabel,
      hasOnboardingV3: (state) => state.indicators.hasOnboardingV3,
      isHomeSideBarShowing: (state) => state.indicators.isHomeSideBarShowing,
      workingMode: (state) => state.workingMode,
    }),
    isDisabled() {
      return this.$route.name === 'Upgrade' || this.$route.name === 'UserCancelSub'
    },
    tabShareWithMeVisible() {
      return this.currentUser.identifyProvider !== 'skoletube'
    },
    isCasualUserNotHaveTeam() {
      return this.currentUser && !this.currentUser.accountId
    },
    isEURegion() {
      return config.region === constants.euRegion
    },
  },
  watch: {
    '$store.state.indicators.hasOnboardingV3': function (newVal) {
      if (newVal === false) {
        setTimeout(() => {
          localStorage.setItem('createTeamTooltipShowed', 'onboarding')
        }, 1000)
      }
    },
  },
  async mounted() {
    try {
      this.sticker = await StickerApi.getSticker()
    } catch (error) {
      Sentry.captureException(error)
    }
    this.openBcollapse()
  },
  methods: {
    ...mapActions(['setIsOnboardingV3ShowingAction']),
    handleLogoutButtonClick() {
      utils.logout(this.currentUser)
      utils.mixpanelTracking(eventCategories.PRESENTATION_LIST, 'Logout')
    },
    hideSide() {
      if (this.hasOnboardingV3) {
        this.setIsOnboardingV3ShowingAction(false)
      }
      if (window.innerWidth > 1023) return
      this.$store.state.indicators.isHomeSideBarShowing = false
    },
    _handleShowWhatsNew() {
      this.$store.state.hasNews = false
      const keyCheckClick = `${this.currentUser.id}-whats-new-check`
      if (!localStorage.getItem(keyCheckClick)) {
        localStorage.setItem(keyCheckClick, moment().format('YYYY MM DD'))
      }
    },
    clickTeamPresentations() {
      this.hideSide()
      const isSelfCreatedTeam = (this.$store.state.currentAccount.createdFrom === 'presenter') || null
      const teamId = (this.$store.state.currentAccount.id) || null
      const teamName = (this.$store.state.currentAccount.name) || null
      utils.mixpanelTracking(eventCategories.PRESENTATION_APP, 'SideNav > Team Presentations', teamName, '', {
        isSelfCreatedTeam,
        teamId,
      })
    },
    clickTeamMembers() {
      this.hideSide()
      const isSelfCreatedTeam = (this.$store.state.currentAccount.createdFrom === 'presenter') || null
      const teamId = (this.$store.state.currentAccount.id) || null
      const teamName = (this.$store.state.currentAccount.name) || null
      utils.mixpanelTracking(eventCategories.PRESENTATION_APP, 'SideNav > Team Members', teamName, '', {
        isSelfCreatedTeam,
        teamId,
      })
    },
    clickWhatsNew() {
      this.hideSide()
      this._handleShowWhatsNew()
      utils.mixpanelTracking(eventCategories.PRESENTATION_LIST, 'SideNav > What\'s New')
    },
    clickHelp() {
      this.hideSide()
      utils.mixpanelTracking(eventCategories.PRESENTATION_LIST, 'SideNav > Help')
    },
    clickFeatureRequest() {
      this.hideSide()
      utils.mixpanelTracking(eventCategories.PRESENTATION_LIST, 'SideNav > Feature Request')
    },
    clickVideo() {
      this.hideSide()
      utils.mixpanelTracking(eventCategories.PRESENTATION_LIST, 'SideNav > Videos')
    },
    clickCreateNewTeam() {
      this.showCreateTeamModal = true
      utils.mixpanelTracking('Presenter App', 'SideNav > Create a team', '', '', {
        plan: this.currentUser.plan,
      })
    },
    hideCreatTeamModal() {
      this.showCreateTeamModal = false
    },
    openBcollapse() {
      setTimeout(() => {
        this.isShowBCollapse = true
      }, 1500)
    },
  },
}
</script>

<style lang="scss">
@import "@/assets/scss/animation.scss";

.bell-ring {
  -webkit-animation: ring 2s .2s ease-in-out infinite;
  -webkit-transform-origin: 50% 4px;
  -moz-animation: ring 2s .2s ease-in-out infinite;
  -moz-transform-origin: 50% 4px;
  animation: ring 2s .2s ease-in-out infinite;
  transform-origin: 50% 4px;
}

.sidenav-profile {
  @apply lg:hidden flex px-4 py-5;
  background-color: #252834;
}

.sidebar {
  @apply text-sm flex flex-col text-base-0 z-10 lg:z-0;
  @apply fixed -left-60 top-0 bottom-0 w-48 overflow-auto;
  @apply lg:flex lg:top-15;
  background-color: #282E3E;
  transition: .15s ease;
  z-index: 22;
}

.sidebar.disabled {
  @apply hidden;
}
.sidebar.show {
  @apply left-14;
}
.sidebar.eu {
  @apply left-0 w-56;
}
.sidebar__item {
  @apply transition-colors text-base-0 text-opacity-60;
  .top {
    @apply relative -top-2.5;
  }
}

.new-window-icon {
  visibility: hidden;
}

.sidebar__item,
.sidebar__item--active {
  @apply h-10 px-2 flex items-center cursor-pointer;

  &.referral {
    @apply h-16;
  }

  &:hover {
    .new-window-icon {
      visibility: visible;
    }
  }
}

.sidebar__item:hover:not(.sidebar__item--active) {
  @apply text-base-0;
}

.sidebar__item--active {
  @apply text-base-0 rounded-lg shadow-nav;
  background-color: #191F2D;

  & svg {
    @apply text-primary-90;
  }
}

.sidebar__item-name {
  @apply ml-2 whitespace-nowrap font-bold;
}

.sidebar__title {
  &:not(.has-collapse) {
    @apply mb-3 mt-1 lg:mt-6 ml-4 font-medium text-xs;
    @apply text-base-0 text-opacity-40 uppercase tracking-widest;
  }

  &.has-collapse {
    @apply flex justify-between;
    @apply mt-3 mb-2 ml-4 font-medium text-xs;

    .text,
    .icon-caret {
      @apply text-base-0 text-opacity-40 uppercase tracking-widest relative;
    }

    &.collapsed {
      .text {
        @apply text-opacity-100;

        &.has-new::after {
          content: '';
          position: absolute;
          top: 5px;
          right: -10px;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background-color: #FF4954;
          box-shadow: 0px 4.00126px 4.00126px rgba(255, 73, 84, 0.3);
        }
      }

      .icon-caret {
        transform: rotateX(180deg);
      }
    }
  }
}

.whats-new__icon-wrapper {
  width: 24px;
}
.credit {
  font-weight: normal;
  font-size: 12px;
}

.sidebar li {
  list-style: none;
}

.create-team--effect-tooltip {
  @media screen and (max-width: 640px) {
    display: none
  }
  @apply shadow-lg;
  width: 14rem;
  height: 12.5rem;
  background: #FFFFFF;
  padding: 8px 12px;
  border-radius: 8px;
  position: absolute;
  left: 4.75rem;
  bottom: -7.5rem;
  .title {
    font-weight: 700;
    font-size: 12px;
    line-height: 23px;
    color: #282E3E;
  }
  span {
    color: #282E3E;
  }
  img {
    margin-top: 8px;
  }
  .effect--tooltip__tip {
    position: absolute;
    width: 7px;
    z-index: 1;
    height: 7px;
    bottom: 50%;
    left: 0px;
    background: #FFFFFF;
    transform: translate(-50%, 0) rotate(45deg);
    border-bottom-right-radius: 2px;
    outline: none;
  }
}
</style>
